/** ISO date (YYYY-MM-DD). Update whenever you change the privacy policy below. */
export const PRIVACY_LAST_UPDATED = '2026-09-03'

export const privacyContent = {
  en: {
    title: 'Storyfy.ge — Privacy Policy',
    lastUpdatedLabel: 'Last updated:',
    intro: [
      'Storyfy.ge respects and protects users’ personal data.',
      'This Privacy Policy explains how information is collected, used, stored, and protected when using the platform — whether you are an organizer with an account, or a guest who simply opens an invitation link.',
      'The data controller is the sole proprietor operating Storyfy.ge, registered under the laws of Georgia. Contact details are at the end of this document.',
    ],
    footer: {
      poweredBy: 'Powered by Storyfy.ge',
      copyright: '© 2026 Storyfy.ge. All rights reserved.',
      contact: 'Contact:',
      termsLink: 'Terms of Service',
      privacyLink: 'Privacy Policy',
    },
    sections: [
      {
        title: '1. What Information We Collect',
        intro: 'We collect the following categories of information:',
        subsections: [
          {
            title: '1.1 Account information (organizers)',
            list: [
              'Full name',
              'Email address',
              'Phone number',
              'Password — stored only as a cryptographic hash, never in readable form',
              'If you sign in with Google: the basic profile data Google returns (name, email address, profile picture). We never receive your Google password.',
              'Bank account details you provide so that we can pay out your balance',
            ],
          },
          {
            title: '1.2 Event information (entered by the organizer)',
            list: [
              'Event title, type, date, time, location, and description',
              'Invitation images, backgrounds, and design settings',
              'The template, colours and fonts chosen for the invitation, and the cover photo — all part of the public invitation page and visible to anyone who holds its link, including in the preview the organizer sees while designing',
              'Wishlist items, target amounts, and currencies',
              'Guest list entries — the name and, if the organizer adds them, the email address and phone number of each invited guest, plus their personal invitation code',
            ],
          },
          {
            title: '1.3 Guest information',
            list: [
              'The name a guest enters when responding to an invitation',
              'Their answer (attending / declined / maybe), the number of companions, and any message they leave for the organizer',
              'The invitation code they use, where the event is invite-only',
              'For gifts: the amount, currency, the wishlist item chosen, the name the guest gives as the sender, and the payment reference returned by the payment provider',
              'For uploads: the display name the guest types, the file itself, and its technical metadata (size, type, upload time)',
            ],
          },
          {
            title: '1.4 Payment information',
            list: [
              'We never see or store full bank card numbers, expiry dates, or CVV codes',
              'From the payment provider we receive only the transaction status, amount, currency, method, and reference identifiers',
              'For direct bank transfers: the payment reference used to reconcile the transfer',
              'Ledger records of commissions withheld, referral bonuses, balances, and payouts',
            ],
          },
          {
            title: '1.5 Information collected automatically',
            list: [
              'IP address',
              'Browser and device information',
              'A session identifier stored in the browser (see Section 2)',
              'Server request logs and technical information related to transactions',
              'Counters used to rate-limit the open guest-upload link and to prevent abuse',
              'Login and activity history',
            ],
          },
        ],
      },
      {
        title: '2. Cookies and Session Storage',
        paragraphs: [
          'Storyfy.ge uses one strictly necessary cookie: the session identifier that keeps you signed in. It is set only after you sign in, is marked HttpOnly (so page scripts cannot read it), and is deleted when you sign out. Where a browser blocks cross-site cookies, the same session token is instead kept in the browser’s own storage so that signing in still works.',
          'Besides the session, the browser’s local storage remembers three preferences on your device: the interface language, the light/dark theme, and — until it is attributed to a sign-up — a referral code from a link you opened. None of these identifies you and none is sent to a third party.',
          'We do not use advertising cookies, tracking pixels, or third-party analytics or profiling services, and we do not build advertising profiles of our users. Because of this, no cookie-consent banner is required to browse the site.',
          'You can delete stored cookies and site data at any time through your browser settings; doing so signs you out.',
        ],
      },
      {
        title: '3. Why We Process Data, and On What Basis',
        intro:
          'Personal data is processed for the following purposes, on the legal bases indicated:',
        list: [
          {
            label: 'To provide the service (performance of a contract):',
            text: 'creating and authenticating accounts, publishing events and invitations, collecting RSVPs, operating the wishlist, processing gifts, running the event album, and generating invitation designs.',
          },
          {
            label: 'To process payments and pay out balances (performance of a contract):',
            text: 'passing the necessary transaction details to the payment provider, recording commissions and referral bonuses, and transferring balances to your bank account.',
          },
          {
            label: 'To keep the platform safe (legitimate interest):',
            text: 'fraud prevention, abuse and spam protection on the open upload link, security monitoring, and investigation of chargebacks or disputes.',
          },
          {
            label: 'To comply with the law (legal obligation):',
            text: 'accounting and tax records, and responding to lawful requests from competent authorities.',
          },
          {
            label: 'To support users and improve the service (legitimate interest):',
            text: 'answering support requests, diagnosing faults, and understanding which features are used — always from our own server records, never through third-party tracking.',
          },
          {
            label: 'With your consent:',
            text: 'anything you optionally choose to add — for example a phone number, an uploaded photo, or the free-text description you give the AI designer. Consent can be withdrawn at any time.',
          },
        ],
      },
      {
        title: '4. The Event Album and Guest Uploads',
        paragraphs: [
          'If an organizer enables guest uploads, photos and videos are sent from the uploader’s browser directly to our storage provider using short-lived, single-purpose links. The files do not pass through our own servers. They are held in a private storage bucket and can only be viewed through temporary signed links issued to the organizer or to the event’s viewers.',
          'The organizer decides whether to open the upload link, who receives it, and what stays in the album; they can delete individual files at any time. We do not pre-moderate uploads.',
          'Album files are kept for the paid storage window (60 days from the first storage purchase for that event), followed by a 30-day grace period during which the album can still be viewed and downloaded. After that, the files are permanently deleted from storage. Deleting the event deletes its album immediately and permanently. We keep no separate backup of album files.',
          'If you appear in a photo or video uploaded by someone else and want it removed, write to us at the address in the final section and we will remove it.',
        ],
      },
      {
        title: '5. The AI Invitation Designer',
        paragraphs: [
          'When an organizer uses the AI invitation designer, the details they supply — the event title, event type, language, and any free-text description they write — are transmitted to a third-party artificial-intelligence provider, which generates a background image, font pairing, and colour palette and returns them to us.',
          'Please do not enter information into the designer that you would not want processed by a third party. If the external provider is unavailable, the design is produced by our own built-in generator and nothing is sent externally.',
          'We keep a record of each generation (the settings used, the resulting design, and whether it was a free or paid generation) for accounting and abuse prevention.',
        ],
      },
      {
        title: '6. Payment Information',
        paragraph:
          'Storyfy.ge does not store full bank card details. Card payments are processed by certified third-party payment providers and acquiring banks in accordance with PCI-DSS security standards and applicable banking regulations. Card data is entered on the provider’s own hosted page and never reaches our servers.',
      },
      {
        title: '7. Sharing of Information',
        intro: 'User information may be shared with:',
        list: [
          'Payment providers and banks — to process gifts, add-on purchases, and payouts',
          'Hosting and technical infrastructure providers — to run the website and its database',
          'Our object storage provider — to store the photos and videos in event albums',
          'Our image hosting provider — to store invitation images and backgrounds',
          'The third-party AI provider — only the design details described in Section 5',
          'Google Fonts — the typefaces used on invitations and on the site are fetched by your browser from Google’s font servers, which therefore see your IP address and browser details',
          'Google Maps — when an organizer searches for a venue, and when a guest taps “open in Google Maps”, the request goes to Google',
          'Unsplash — stock cover photographs are loaded by your browser from Unsplash’s servers',
          'Google sign-in — only if you choose to sign in with your Google account',
          'Government and law enforcement authorities when required by law',
        ],
        outro:
          'Storyfy.ge does not sell users’ personal information to third parties, and does not share it with advertising networks or data brokers. Service providers act on our instructions and only for the purposes described above.',
      },
      {
        title: '8. International Transfers',
        paragraph:
          'Some of our service providers (hosting, storage, the AI provider, and the Google and Unsplash services your browser contacts directly) process data on servers outside Georgia, including in the European Union and the United States. Where that happens, we rely on providers that offer appropriate contractual and technical safeguards for the protection of personal data.',
      },
      {
        title: '9. Data Retention Period',
        intro:
          'Personal information is retained only for as long as necessary for legal, accounting, operational, security, or contractual purposes. In particular:',
        list: [
          'Account data — while the account exists, and afterwards only where a legal obligation requires it',
          'Event, guest list, and RSVP data — until the organizer deletes the event or the account',
          'Album files — for the storage window plus the grace period, or until the event is deleted, whichever comes first',
          'Payment and ledger records — for the period required by accounting and tax law, even after an event is deleted',
          'Password recovery requests — until they are resolved or dismissed',
          'Server and security logs — for a limited period proportionate to their security purpose',
        ],
      },
      {
        title: '10. User Rights',
        intro: 'Users have the right to request:',
        list: [
          'Access to their personal data, and information about how it is processed',
          'Correction of inaccurate or incomplete information',
          'Deletion of data within the limits permitted by law',
          'Restriction of data processing in specific cases',
          'Objection to processing based on our legitimate interests',
          'A copy of the data they provided, in a commonly used format',
          'Withdrawal of a consent previously given, without affecting processing already carried out',
        ],
        outro:
          'Requests may be submitted by email to the address below; we respond within the period set by law. Users also have the right to lodge a complaint with the Personal Data Protection Service of Georgia.',
      },
      {
        title: '11. Security Measures',
        paragraphs: [
          'We use reasonable technical and organizational security measures to protect user information: passwords are stored only as cryptographic hashes, sessions use HttpOnly cookies, traffic is served over HTTPS, album files sit in a private bucket reachable only through short-lived signed links, and administrative access is limited to authorised operators.',
          'We will never ask you for your password by phone, email, or message, and we do not send automated password-reset links. If you forget your password, your request is handled by our support team, who verify ownership of the account and issue a temporary password through a separate channel.',
          'The invitation preview shown while an organizer designs their event is their own invitation page rendered locally in the browser; producing it sends nothing beyond what viewing the invitation itself would.',
          'However, data transmission over the internet cannot be guaranteed to be completely secure.',
        ],
      },
      {
        title: '12. Children',
        paragraph:
          'The platform is not directed at children, and accounts may only be created by adults. Where an event involves photographs or personal data of minors, the organizer is responsible for having the consent of a parent or legal guardian before uploading or sharing such material.',
      },
      {
        title: '13. Third-Party Links',
        paragraph:
          'The platform may contain links to third-party websites or services. Storyfy.ge is not responsible for the privacy policies or content of third-party resources.',
      },
      {
        title: '14. Changes to This Policy',
        paragraph:
          'Storyfy.ge reserves the right to update this Privacy Policy at any time. The updated version becomes effective immediately upon publication on the website. The date at the top of this page always shows when it was last changed.',
      },
      {
        title: '15. Contact Information',
        contact: {
          emailLabel: 'Email:',
          websiteLabel: 'Website:',
        },
      },
    ],
  },
  ka: {
    title: 'Storyfy.ge — კონფიდენციალურობის პოლიტიკა',
    lastUpdatedLabel: 'ბოლო განახლების თარიღი:',
    intro: [
      'Storyfy.ge პატივს სცემს და იცავს მომხმარებლების პერსონალურ მონაცემებს.',
      'წინამდებარე კონფიდენციალურობის პოლიტიკა განმარტავს, თუ როგორ ხდება მონაცემების შეგროვება, გამოყენება, შენახვა და დაცვა პლატფორმის გამოყენებისას — როგორც რეგისტრირებული ორგანიზატორის, ისე იმ სტუმრის შემთხვევაში, რომელიც უბრალოდ ხსნის მოწვევის ბმულს.',
      'მონაცემთა დამმუშავებელია Storyfy.ge-ის ოპერირებაზე პასუხისმგებელი, საქართველოს კანონმდებლობის შესაბამისად რეგისტრირებული ინდივიდუალური მეწარმე. საკონტაქტო ინფორმაცია მოცემულია დოკუმენტის ბოლოს.',
    ],
    footer: {
      poweredBy: 'Storyfy.ge-ით',
      copyright: '© 2026 Storyfy.ge. ყველა უფლება დაცულია.',
      contact: 'საკონტაქტო:',
      termsLink: 'მომსახურების წესები და პირობები',
      privacyLink: 'კონფიდენციალურობის პოლიტიკა',
    },
    sections: [
      {
        title: '1. რა ინფორმაციას ვაგროვებთ',
        intro: 'ჩვენ ვაგროვებთ ინფორმაციის შემდეგ კატეგორიებს:',
        subsections: [
          {
            title: '1.1 ანგარიშის მონაცემები (ორგანიზატორები)',
            list: [
              'სახელი და გვარი',
              'ელ-ფოსტის მისამართი',
              'ტელეფონის ნომერი',
              'პაროლი — ინახება მხოლოდ კრიპტოგრაფიული ჰეშის სახით და არასდროს წაკითხვად ფორმატში',
              'Google-ით ავტორიზაციისას: Google-ის მიერ დაბრუნებული საბაზისო პროფილის მონაცემები (სახელი, ელ-ფოსტა, პროფილის სურათი). თქვენი Google-ის პაროლი ჩვენთვის ხელმისაწვდომი არ ხდება.',
              'საბანკო ანგარიშის მონაცემები, რომელსაც თქვენ გვაწვდით ბალანსის გადასარიცხად',
            ],
          },
          {
            title: '1.2 ღონისძიების მონაცემები (შეაქვს ორგანიზატორს)',
            list: [
              'ღონისძიების დასახელება, ტიპი, თარიღი, დრო, ადგილი და აღწერა',
              'მოწვევის სურათები, ფონები და დიზაინის პარამეტრები',
              'მოწვევისთვის არჩეული შაბლონი, ფერები და შრიფტები, ასევე ფონის ფოტო — ყველა ეს საჯარო მოწვევის გვერდის ნაწილია და ხილულია ბმულის ნებისმიერი მფლობელისთვის, მათ შორის იმ გადახედვაში, რომელსაც ორგანიზატორი დიზაინის დროს ხედავს',
              'ვიშლისტის პოზიციები, სასურველი თანხები და ვალუტები',
              'სტუმრების სია — თითოეული მოწვეულის სახელი და, ორგანიზატორის მიერ მითითების შემთხვევაში, ელ-ფოსტა და ტელეფონის ნომერი, ასევე მისი პირადი მოსაწვევი კოდი',
            ],
          },
          {
            title: '1.3 სტუმრის მონაცემები',
            list: [
              'სახელი, რომელსაც სტუმარი უთითებს მოწვევაზე პასუხის გაცემისას',
              'მისი პასუხი (დავესწრები / ვერ დავესწრები / ჯერ არ ვიცი), თანმხლებ პირთა რაოდენობა და ორგანიზატორისთვის დატოვებული შეტყობინება',
              'მოსაწვევი კოდი, თუ ღონისძიება მხოლოდ მოწვეულთათვისაა',
              'საჩუქრის შემთხვევაში: თანხა, ვალუტა, არჩეული ვიშლისტის პოზიცია, გამგზავნის სახელი და საგადახდო პროვაიდერის მიერ დაბრუნებული ტრანზაქციის იდენტიფიკატორი',
              'ატვირთვის შემთხვევაში: სტუმრის მიერ მითითებული სახელი, თავად ფაილი და მისი ტექნიკური მეტამონაცემები (ზომა, ტიპი, ატვირთვის დრო)',
            ],
          },
          {
            title: '1.4 საგადახდო ინფორმაცია',
            list: [
              'ჩვენ არასდროს ვხედავთ და არ ვინახავთ ბარათის სრულ ნომერს, მოქმედების ვადას ან CVV კოდს',
              'საგადახდო პროვაიდერისგან ვიღებთ მხოლოდ ტრანზაქციის სტატუსს, თანხას, ვალუტას, გადახდის მეთოდსა და საიდენტიფიკაციო ნომრებს',
              'პირდაპირი საბანკო გადარიცხვისას — გადახდის დანიშნულების კოდს, რომლითაც ხდება გადარიცხვის იდენტიფიცირება',
              'დაკავებული საკომისიოს, სარეფერალო ბონუსების, ბალანსებისა და გაცემული თანხების აღრიცხვის ჩანაწერებს',
            ],
          },
          {
            title: '1.5 ავტომატურად შეგროვებული ინფორმაცია',
            list: [
              'IP მისამართი',
              'ბრაუზერისა და მოწყობილობის ინფორმაცია',
              'ბრაუზერში დაცული სესიის იდენტიფიკატორი (იხ. მე-2 თავი)',
              'სერვერის მოთხოვნების ჟურნალი და ტრანზაქციებთან დაკავშირებული ტექნიკური ინფორმაცია',
              'მრიცხველები, რომლებიც ზღუდავს ღია ატვირთვის ბმულის სიხშირეს და იცავს მას ბოროტად გამოყენებისგან',
              'ავტორიზაციისა და აქტივობის ისტორია',
            ],
          },
        ],
      },
      {
        title: '2. Cookies და სესიის მონაცემები',
        paragraphs: [
          'Storyfy.ge იყენებს მხოლოდ ერთ, აუცილებელ Cookie-ს: სესიის იდენტიფიკატორს, რომელიც სისტემაში შესულ მდგომარეობაში გინარჩუნებთ. იგი ეწერება მხოლოდ ავტორიზაციის შემდეგ, აღნიშნულია როგორც HttpOnly (რაც გვერდის სკრიპტებს მის წაკითხვას უკრძალავს) და იშლება სისტემიდან გამოსვლისას. თუ ბრაუზერი ბლოკავს მესამე მხარის Cookie-ებს, იგივე სესიის ტოკენი ინახება ბრაუზერის საკუთარ მეხსიერებაში, რათა ავტორიზაცია მაინც მუშაობდეს.',
          'სესიის გარდა, ბრაუზერის ლოკალური მეხსიერება თქვენს მოწყობილობაზე ინახავს სამ პარამეტრს: ინტერფეისის ენას, ღია/მუქ თემას და — რეგისტრაციაზე მიბმამდე — გახსნილი ბმულიდან მიღებულ სარეფერალო კოდს. არცერთი მათგანი არ გაიდენტიფიცირებთ და არცერთი არ იგზავნება მესამე მხარესთან.',
          'ჩვენ არ ვიყენებთ სარეკლამო Cookie-ებს, თვალთვალის პიქსელებს, მესამე მხარის ანალიტიკის ან პროფილირების სერვისებს და არ ვქმნით მომხმარებელთა სარეკლამო პროფილებს. სწორედ ამიტომ საიტით სარგებლობა არ საჭიროებს Cookie-ებზე თანხმობის ბანერს.',
          'შენახული Cookie-ებისა და საიტის მონაცემების წაშლა ნებისმიერ დროს შეგიძლიათ ბრაუზერის პარამეტრებიდან; ამის შედეგად სისტემიდან გახვალთ.',
        ],
      },
      {
        title: '3. მონაცემების დამუშავების მიზნები და საფუძვლები',
        intro:
          'პერსონალური მონაცემები მუშავდება შემდეგი მიზნებისთვის, მითითებული სამართლებრივი საფუძვლებით:',
        list: [
          {
            label: 'სერვისის მიწოდებისთვის (ხელშეკრულების შესრულება):',
            text: 'ანგარიშის შექმნა და ავტორიზაცია, ღონისძიებებისა და მოწვევების გამოქვეყნება, დასწრების დადასტურებების მიღება, ვიშლისტის ფუნქციონირება, საჩუქრების დამუშავება, ღონისძიების ალბომის მუშაობა და მოწვევის დიზაინის გენერირება.',
          },
          {
            label: 'გადახდებისა და თანხის გაცემისთვის (ხელშეკრულების შესრულება):',
            text: 'საგადახდო პროვაიდერისთვის აუცილებელი ტრანზაქციული მონაცემების გადაცემა, საკომისიოსა და სარეფერალო ბონუსების აღრიცხვა და ბალანსის თქვენს საბანკო ანგარიშზე გადარიცხვა.',
          },
          {
            label: 'პლატფორმის უსაფრთხოებისთვის (ლეგიტიმური ინტერესი):',
            text: 'თაღლითობის პრევენცია, ღია ატვირთვის ბმულის დაცვა ბოროტად გამოყენებისა და სპამისგან, უსაფრთხოების მონიტორინგი და ჩარჯბექების თუ დავების შესწავლა.',
          },
          {
            label: 'კანონმდებლობის მოთხოვნების შესასრულებლად (სამართლებრივი ვალდებულება):',
            text: 'საბუღალტრო და საგადასახადო აღრიცხვა, ასევე უფლებამოსილი ორგანოების კანონიერ მოთხოვნებზე რეაგირება.',
          },
          {
            label: 'მომხმარებელთა მხარდაჭერისა და სერვისის გაუმჯობესებისთვის (ლეგიტიმური ინტერესი):',
            text: 'მხარდაჭერის მოთხოვნებზე პასუხი, ხარვეზების დიაგნოსტიკა და იმის გაანალიზება, თუ რომელი ფუნქციები გამოიყენება — ყოველთვის ჩვენივე სერვერული ჩანაწერების საფუძველზე და არასდროს მესამე მხარის თვალთვალის საშუალებით.',
          },
          {
            label: 'თქვენი თანხმობით:',
            text: 'ყველაფერი, რასაც ნებაყოფლობით ამატებთ — მაგალითად ტელეფონის ნომერი, ატვირთული ფოტო ან ხელოვნური ინტელექტის დიზაინერისთვის მითითებული თავისუფალი ტექსტი. თანხმობის გამოხმობა ნებისმიერ დროს შეგიძლიათ.',
          },
        ],
      },
      {
        title: '4. ღონისძიების ალბომი და სტუმრების ატვირთვები',
        paragraphs: [
          'თუ ორგანიზატორი ჩართავს სტუმრების ატვირთვის ფუნქციას, ფოტოები და ვიდეოები მატვირთავის ბრაუზერიდან პირდაპირ ეგზავნება ჩვენს საცავის პროვაიდერს მოკლევადიანი, ერთჯერადი დანიშნულების ბმულებით. ფაილები არ გაივლის ჩვენს სერვერებს. ისინი ინახება დახურულ (private) საცავში და ხელმისაწვდომია მხოლოდ ორგანიზატორისთვის ან ღონისძიების მნახველებისთვის გაცემული დროებითი, ხელმოწერილი ბმულებით.',
          'ორგანიზატორი წყვეტს, გახსნას თუ არა ატვირთვის ბმული, ვის გაუზიაროს იგი და რა დარჩეს ალბომში; მას ნებისმიერ დროს შეუძლია ცალკეული ფაილის წაშლა. ჩვენ ატვირთვების წინასწარ მოდერაციას არ ვახორციელებთ.',
          'ალბომის ფაილები ინახება ანაზღაურებული სამომსახურო ვადის განმავლობაში (60 დღე ღონისძიებაზე პირველი შეძენიდან), რასაც მოჰყვება 30-დღიანი საშეღავათო ვადა, როცა ალბომის ნახვა და ჩამოტვირთვა კვლავ შესაძლებელია. ამის შემდეგ ფაილები სამუდამოდ იშლება საცავიდან. ღონისძიების წაშლა მის ალბომს შლის დაუყოვნებლივ და შეუქცევადად. ალბომის ფაილების ცალკე სარეზერვო ასლს ჩვენ არ ვინახავთ.',
          'თუ თქვენ აღმოჩნდით სხვისი ატვირთული ფოტოს ან ვიდეოს ამსახველ მასალაზე და გსურთ მისი წაშლა, მოგვწერეთ დოკუმენტის ბოლოს მითითებულ მისამართზე და ჩვენ წავშლით მას.',
        ],
      },
      {
        title: '5. ხელოვნური ინტელექტის დიზაინერი',
        paragraphs: [
          'როდესაც ორგანიზატორი იყენებს ხელოვნური ინტელექტის დიზაინერს, მის მიერ მითითებული მონაცემები — ღონისძიების დასახელება, ტიპი, ენა და ნებისმიერი თავისუფალი ტექსტი — გადაეცემა ხელოვნური ინტელექტის მესამე მხარე პროვაიდერს, რომელიც ქმნის ფონურ გამოსახულებას, შრიფტების წყვილსა და ფერთა პალიტრას და გვიბრუნებს მათ.',
          'გთხოვთ, დიზაინერში არ მიუთითოთ ინფორმაცია, რომლის მესამე მხარის მიერ დამუშავებაც არ გსურთ. გარე პროვაიდერის მიუწვდომლობის შემთხვევაში დიზაინს ქმნის ჩვენი ჩაშენებული გენერატორი და გარეთ არაფერი იგზავნება.',
          'ჩვენ ვინახავთ თითოეული გენერაციის ჩანაწერს (გამოყენებული პარამეტრები, მიღებული დიზაინი და ის, უფასო იყო თუ ანაზღაურებადი) აღრიცხვისა და ბოროტად გამოყენების პრევენციის მიზნით.',
        ],
      },
      {
        title: '6. საგადახდო ინფორმაცია',
        paragraph:
          'Storyfy.ge არ ინახავს საბანკო ბარათის სრულ მონაცემებს. ბარათით გადახდები მუშავდება სერტიფიცირებული მესამე მხარის საგადახდო პროვაიდერებისა და ეკვაირინგ ბანკების მიერ, რომლებიც მოქმედებენ PCI-DSS უსაფრთხოების სტანდარტებისა და საბანკო რეგულაციების შესაბამისად. ბარათის მონაცემები შეიტანება უშუალოდ პროვაიდერის გვერდზე და ჩვენს სერვერებამდე არ აღწევს.',
      },
      {
        title: '7. ინფორმაციის გაზიარება',
        intro: 'მომხმარებლის ინფორმაცია შესაძლოა გაზიარებულ იქნას:',
        list: [
          'საგადახდო პროვაიდერებთან და ბანკებთან — საჩუქრების, დამატებითი სერვისების შეძენისა და თანხის გაცემის დასამუშავებლად',
          'ჰოსტინგისა და ტექნიკური ინფრასტრუქტურის მომწოდებლებთან — ვებ-გვერდისა და მისი მონაცემთა ბაზის ფუნქციონირებისთვის',
          'ფაილების საცავის პროვაიდერთან — ღონისძიების ალბომში ატვირთული ფოტო-ვიდეო მასალის შესანახად',
          'სურათების ჰოსტინგის პროვაიდერთან — მოწვევის სურათებისა და ფონების შესანახად',
          'ხელოვნური ინტელექტის მესამე მხარე პროვაიდერთან — მხოლოდ მე-5 თავში აღწერილი დიზაინის მონაცემები',
          'Google Fonts-თან — მოწვევებსა და საიტზე გამოყენებულ შრიფტებს თქვენი ბრაუზერი Google-ის შრიფტების სერვერებიდან იტვირთავს, რომლებიც შესაბამისად ხედავენ თქვენს IP მისამართსა და ბრაუზერის მონაცემებს',
          'Google Maps-თან — როცა ორგანიზატორი ეძებს ადგილს, ან სტუმარი აჭერს „Google Maps-ში გახსნას“, მოთხოვნა Google-თან მიდის',
          'Unsplash-თან — ფონის სასაქონლო ფოტოებს თქვენი ბრაუზერი Unsplash-ის სერვერებიდან იტვირთავს',
          'Google-ის ავტორიზაციასთან — მხოლოდ იმ შემთხვევაში, თუ თქვენ თავად აირჩევთ Google-ის ანგარიშით ავტორიზაციას',
          'სახელმწიფო და სამართალდამცავ ორგანოებთან, კანონით მოთხოვნილ შემთხვევებში',
        ],
        outro:
          'Storyfy.ge არ ყიდის მომხმარებლების პერსონალურ ინფორმაციას მესამე პირებზე და არ უზიარებს მას სარეკლამო ქსელებს ან მონაცემთა ბროკერებს. მომსახურების მიმწოდებლები მოქმედებენ ჩვენი მითითებით და მხოლოდ ზემოთ აღწერილი მიზნებისთვის.',
      },
      {
        title: '8. მონაცემების საერთაშორისო გადაცემა',
        paragraph:
          'ჩვენი ზოგიერთი მომსახურების მიმწოდებელი (ჰოსტინგი, ფაილების საცავი, ხელოვნური ინტელექტის პროვაიდერი, ასევე Google-ისა და Unsplash-ის სერვისები, რომლებსაც თქვენი ბრაუზერი უშუალოდ უკავშირდება) მონაცემებს ამუშავებს საქართველოს ფარგლებს გარეთ არსებულ სერვერებზე, მათ შორის ევროკავშირსა და აშშ-ში. ასეთ შემთხვევებში ვთანამშრომლობთ იმ პროვაიდერებთან, რომლებიც უზრუნველყოფენ პერსონალურ მონაცემთა დაცვის შესაბამის სახელშეკრულებო და ტექნიკურ გარანტიებს.',
      },
      {
        title: '9. მონაცემების შენახვის ვადა',
        intro:
          'პერსონალური ინფორმაცია ინახება მხოლოდ იმ ვადით, რაც აუცილებელია სამართლებრივი, საბუღალტრო, ოპერაციული, უსაფრთხოების ან სახელშეკრულებო მიზნებისთვის. კერძოდ:',
        list: [
          'ანგარიშის მონაცემები — ანგარიშის არსებობის განმავლობაში, ხოლო შემდგომ მხოლოდ მაშინ, თუ ამას კანონი ითხოვს',
          'ღონისძიების, სტუმრების სიისა და დასწრების დადასტურებების მონაცემები — სანამ ორგანიზატორი არ წაშლის ღონისძიებას ან ანგარიშს',
          'ალბომის ფაილები — სამომსახურო ვადისა და საშეღავათო ვადის განმავლობაში, ან ღონისძიების წაშლამდე, რომელიც უფრო ადრე დადგება',
          'საგადახდო და აღრიცხვის ჩანაწერები — საბუღალტრო და საგადასახადო კანონმდებლობით დადგენილი ვადით, ღონისძიების წაშლის შემდეგაც',
          'პაროლის აღდგენის მოთხოვნები — მათ დაკმაყოფილებამდე ან გაუქმებამდე',
          'სერვერისა და უსაფრთხოების ჟურნალები — შეზღუდული, უსაფრთხოების მიზნის პროპორციული ვადით',
        ],
      },
      {
        title: '10. მომხმარებლის უფლებები',
        intro: 'მომხმარებელს უფლება აქვს მოითხოვოს:',
        list: [
          'საკუთარ პერსონალურ მონაცემებზე წვდომა და ინფორმაცია მათი დამუშავების შესახებ',
          'არასწორი ან არასრული ინფორმაციის გასწორება',
          'მონაცემების წაშლა, კანონით დაშვებულ ფარგლებში',
          'მონაცემთა დამუშავების შეზღუდვა კონკრეტულ შემთხვევებში',
          'ჩვენს ლეგიტიმურ ინტერესზე დაფუძნებული დამუშავების გასაჩივრება',
          'მის მიერ მოწოდებული მონაცემების ასლი ფართოდ გავრცელებულ ფორმატში',
          'ადრე გაცემული თანხმობის გამოხმობა, უკვე განხორციელებულ დამუშავებაზე გავლენის გარეშე',
        ],
        outro:
          'მოთხოვნის გაგზავნა შესაძლებელია ქვემოთ მითითებულ ელ-ფოსტაზე; პასუხს გაგცემთ კანონით დადგენილ ვადაში. მომხმარებელს ასევე უფლება აქვს, საჩივრით მიმართოს საქართველოს პერსონალურ მონაცემთა დაცვის სამსახურს.',
      },
      {
        title: '11. უსაფრთხოების ზომები',
        paragraphs: [
          'ჩვენ ვიყენებთ გონივრულ ტექნიკურ და ორგანიზაციულ უსაფრთხოების ზომებს მომხმარებლის ინფორმაციის დასაცავად: პაროლები ინახება მხოლოდ კრიპტოგრაფიული ჰეშის სახით, სესიები იყენებს HttpOnly Cookie-ებს, ტრაფიკი გადაიცემა HTTPS-ით, ალბომის ფაილები განთავსებულია დახურულ საცავში და ხელმისაწვდომია მხოლოდ მოკლევადიანი ხელმოწერილი ბმულებით, ხოლო ადმინისტრაციული წვდომა შეზღუდულია უფლებამოსილი პირებით.',
          'ჩვენ არასდროს გკითხავთ პაროლს ტელეფონით, ელ-ფოსტით ან შეტყობინებით და არ ვაგზავნით პაროლის აღდგენის ავტომატურ ბმულებს. პაროლის დავიწყების შემთხვევაში თქვენს მოთხოვნას ამუშავებს მხარდაჭერის გუნდი, რომელიც ადასტურებს ანგარიშზე თქვენს უფლებას და დროებით პაროლს ცალკე არხით გადმოგცემთ.',
          'გადახედვა, რომელსაც ორგანიზატორი ღონისძიების დიზაინის დროს ხედავს, მისივე მოწვევის გვერდია, რომელიც ბრაუზერში ლოკალურად იხატება; მისი შექმნა არაფერს აგზავნის იმაზე მეტს, რასაც თავად მოწვევის ნახვა გააგზავნიდა.',
          'თუმცა, ინტერნეტში მონაცემთა გადაცემა სრულ უსაფრთხოებას ვერ უზრუნველყოფს.',
        ],
      },
      {
        title: '12. არასრულწლოვნები',
        paragraph:
          'პლატფორმა არ არის განკუთვნილი ბავშვებისთვის და ანგარიშის შექმნა შეუძლიათ მხოლოდ სრულწლოვან პირებს. თუ ღონისძიება მოიცავს არასრულწლოვნის ფოტოს ან პერსონალურ მონაცემებს, ორგანიზატორი პასუხისმგებელია, რომ ასეთი მასალის ატვირთვამდე ან გაზიარებამდე მოპოვებული ჰქონდეს მშობლის ან კანონიერი წარმომადგენლის თანხმობა.',
      },
      {
        title: '13. მესამე მხარის ბმულები',
        paragraph:
          'პლატფორმა შესაძლოა შეიცავდეს მესამე მხარის ვებ-გვერდების ან სერვისების ბმულებს. Storyfy.ge არ არის პასუხისმგებელი მესამე მხარის რესურსების კონფიდენციალურობის პოლიტიკასა ან შინაარსზე.',
      },
      {
        title: '14. პოლიტიკის ცვლილებები',
        paragraph:
          'Storyfy.ge იტოვებს უფლებას ნებისმიერ დროს განაახლოს წინამდებარე კონფიდენციალურობის პოლიტიკა. განახლებული ვერსია ძალაში შედის ვებ-გვერდზე გამოქვეყნებისთანავე. გვერდის თავში მითითებული თარიღი ყოველთვის ასახავს ბოლო ცვლილების დროს.',
      },
      {
        title: '15. საკონტაქტო ინფორმაცია',
        contact: {
          emailLabel: 'ელ-ფოსტა:',
          websiteLabel: 'ვებ-გვერდი:',
        },
      },
    ],
  },
}
